import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  type Unsubscribe,
  type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth } from '../lib/firebase/auth';
import { db, tsToDate } from '../lib/firebase/firestore';
import { COLLECTIONS } from '../config/constants';
import type { AdminUser, AccountType } from '../types/auth';
import type { User, DateFormatOption } from '../types/user';
import {
  deriveRoleFromAccountType,
  deriveIsBetaTesterFromAccountType,
  getEffectiveAccountType,
} from '../types/user';

function mapSettings(raw: unknown): User['settings'] {
  if (raw && typeof raw === 'object') {
    const s = raw as Record<string, unknown>;
    return {
      baseCurrency: (s.baseCurrency as string) ?? (s.currency as string) ?? 'ILS',
      dealCurrency: (s.dealCurrency as string) ?? 'USD',
      loanCurrency: (s.loanCurrency as string) ?? 'ILS',
      emailNewsletter: s.emailNewsletter === true,
      paymentReminders: s.paymentReminders === true,
      dateFormat: (s.dateFormat as DateFormatOption) ?? 'eu',
      country: (s.country as string) ?? 'IL',
    };
  }
  return {
    baseCurrency: 'ILS',
    dealCurrency: 'USD',
    loanCurrency: 'ILS',
    emailNewsletter: false,
    paymentReminders: false,
    dateFormat: 'eu',
    country: 'IL',
  };
}

export const authService = {
  async login(email: string, password: string): Promise<AdminUser> {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const userData = await authService.fetchAdminUser(cred.user.uid);
    if (!userData) {
      await signOut(auth);
      throw new Error('User data not found');
    }
    if (!userData.isAdmin) {
      await signOut(auth);
      throw new Error('Access denied. Admin only.');
    }
    return userData;
  },

  async logout(): Promise<void> {
    await signOut(auth);
  },

  async fetchAdminUser(uid: string): Promise<AdminUser | null> {
    const snap = await getDoc(doc(db, COLLECTIONS.userData, uid));
    if (!snap.exists()) return null;
    const data = snap.data();
    return {
      id: snap.id,
      email: data.email ?? '',
      displayName: data.displayName ?? null,
      isAdmin: data.isAdmin === true,
      accountType: (data.accountType as AccountType) ?? 'regular',
    };
  },

  async fetchFullUser(uid: string): Promise<User | null> {
    const snap = await getDoc(doc(db, COLLECTIONS.userData, uid));
    if (!snap.exists()) return null;
    const data = snap.data();
    const storedAccountType = (data.accountType as AccountType) ?? 'regular';
    const accountTypeEndDate = tsToDate(data.accountTypeEndDate);
    const previousAccountType = (data.previousAccountType as AccountType) ?? null;
    const effectiveAccountType = getEffectiveAccountType(
      storedAccountType,
      accountTypeEndDate,
      previousAccountType,
    );
    return {
      id: snap.id,
      email: data.email ?? '',
      displayName: data.displayName ?? null,
      createdAt: tsToDate(data.createdAt) ?? new Date(),
      lastLoginAt: tsToDate(data.lastLoginAt),
      accountType: effectiveAccountType,
      accountTypeEndDate,
      previousAccountType,
      role: deriveRoleFromAccountType(effectiveAccountType),
      isAdmin: data.isAdmin === true,
      isBetaTester: deriveIsBetaTesterFromAccountType(effectiveAccountType),
      isDeleted: data.isDeleted === true,
      deletedAt: tsToDate(data.deletedAt),
      preferredInvestmentTypes: data.preferredInvestmentTypes ?? [],
      settings: mapSettings(data.settings),
      acceptedTosVersion: data.acceptedTosVersion ?? null,
      tosAcceptedAt: tsToDate(data.tosAcceptedAt),
      metadata: data.metadata ?? {},
    };
  },

  onAuthStateChanged(callback: (user: FirebaseUser | null) => void): Unsubscribe {
    return onAuthStateChanged(auth, callback);
  },

  async sendPasswordReset(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email);
  },
};
